import { useState } from 'react';
import { Button, DateInput, FORM_LABEL_CLASS, Modal } from '@safetyhub/ui';
import { EditModalFooter } from '../common/EditModalFooter';

export interface PlanDateModalProps {
  /** 의무 이름(대행 현장이면 기관 이름 — 호출부가 `agencyDisplayTitle`로) */
  obligationTitle: string;
  /** 대상 하위 줄이면 그 대상 이름(사람·설비·공정) — 현장 단위면 생략 */
  subjectLabel?: string;
  /** 칸이 덮는 해·달(연간 격자의 칸) — 날짜 선택의 하한·상한 */
  year: number;
  startMonth: number;
  endMonth: number;
  /** 지금 계획일(`yyyy-MM-dd` · 현장 시간대) — 없으면 null */
  initialDate: string | null;
  /** 계획이 없을 때의 초기값(`yyyy-MM-dd`) */
  defaultDate: string;
  submitting: boolean;
  errorMessage: string | null;
  /** 날짜를 고르면 그 날, 「계획 지우기」면 null */
  onSubmit: (plannedOn: string | null) => void;
  onClose: () => void;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** 그 해 그 달의 마지막 날(1~12월) — 달력 산수라 시간대와 무관하다(다음 달 0일 = 이달 말일) */
function lastDayOf(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/**
 * 계획일(②-c E8 · 설계 2차 §7) — 연간 격자의 칸 하나(기간 하나)에 「언제 할 계획인가」를 적는다. 계획은 이행이 아니다
 * (이행은 문서 등록이 정한다) — 칸의 ○ 표식이 이 값이다. 기간이 아직 실체화되지 않았으면 서버가 이 행위로 만든다(멱등).
 *
 * 날짜 범위는 칸이 덮는 달로 좁힌다(편의 — 벽은 서버다: 기준일부터 도는 기간은 달 중간에서 시작할 수 있고, 그 밖이면 서버가 「계획일이
 * 그 기간 밖입니다」로 거부해 이 창에 그대로 보인다).
 */
export default function PlanDateModal({
  obligationTitle,
  subjectLabel,
  year,
  startMonth,
  endMonth,
  initialDate,
  defaultDate,
  submitting,
  errorMessage,
  onSubmit,
  onClose,
}: PlanDateModalProps) {
  const [plannedOn, setPlannedOn] = useState(initialDate ?? defaultDate);
  const min = `${year}-${pad(startMonth)}-01`;
  const max = `${year}-${pad(endMonth)}-${pad(lastDayOf(year, endMonth))}`;
  // 비교 기준은 «창을 열 때 칸에 든 값»이다 — 계획이 없으면 defaultDate가 미리 채워지므로 그것과 비교해야
  // 손대지 않고 「취소」를 눌렀을 때 「저장하지 않은 변경사항」 확인이 뜨지 않는다(2026-11-03 dev 실측 발견).
  const isDirty = plannedOn !== (initialDate ?? defaultDate);
  const blockedReason = !plannedOn ? '계획일을 선택하세요.' : plannedOn === initialDate ? '바뀐 것이 없습니다.' : '';
  const range = startMonth === endMonth ? `${year}년 ${startMonth}월` : `${year}년 ${startMonth}~${endMonth}월`;

  return (
    <Modal
      open
      onClose={onClose}
      title="계획일"
      size="sm"
      closeLocked={submitting}
      isDirty={isDirty}
      footer={
        <EditModalFooter
          isDirty={isDirty}
          blockedReason={blockedReason}
          submitLabel="계획 저장"
          submitDisabled={!!blockedReason || submitting}
          busy={submitting}
          busyLabel="저장 중..."
          onSubmit={() => {
            if (blockedReason || submitting) return;
            onSubmit(plannedOn);
          }}
          onFallbackClose={onClose}
        />
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-700">
          <span className="font-bold text-ink-900">{obligationTitle}</span>
          {subjectLabel ? ` · ${subjectLabel}` : ''}의 {range} 기간에 할 날을 적습니다. 계획은 이행이 아닙니다 — 이행은 문서를 등록해야
          기록됩니다.
        </p>
        <div>
          <label htmlFor="legal-plan-date" className={FORM_LABEL_CLASS}>
            계획일
          </label>
          <DateInput
            id="legal-plan-date"
            label="계획일"
            value={plannedOn}
            onChange={setPlannedOn}
            min={min}
            max={max}
            disabled={submitting}
            className="w-full"
          />
        </div>
        {initialDate && (
          <Button size="sm" variant="secondary" disabled={submitting} onClick={() => onSubmit(null)}>
            계획 지우기
          </Button>
        )}
        {errorMessage && (
          <p role="alert" className="rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {errorMessage}
          </p>
        )}
      </div>
    </Modal>
  );
}
